import React, { useState, useEffect, useRef } from 'react';
import { FaGithub } from 'react-icons/fa';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "./shared/card";
import { Input } from "./shared/input";
import { Button } from "./shared/button";
import { GoogleSignInButton } from './GoogleSignInButton';
import { Modal } from './Modal';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: (email: string, password: string) => Promise<void>;
  onRegister: (email: string, password: string) => Promise<void>;
  onAuthSuccess: () => void;
}

export const LoginModal: React.FC<LoginModalProps> = ({
  isOpen,
  onClose,
  onLogin,
  onRegister,
  onAuthSuccess,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    email: string;
    password: string;
    general: string | JSX.Element;
  }>({ email: '', password: '', general: '' });
  const [touchedFields, setTouchedFields] = useState({ email: false, password: false });
  const [showPassword, setShowPassword] = useState(false);
  const [errorType, setErrorType] = useState<'already_registered' | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isLoading) return;

    setErrorType(null);

    const trimmedEmail = email.trim();
    if (trimmedEmail !== email) {
      setEmail(trimmedEmail);
    }

    if (!validateForm()) return;

    setIsLoading(true);
    try {
      if (isRegistering) {
        await onRegister(email, password);
      } else {
        await onLogin(email, password);
      }
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === 'Email already registered') {
          setErrorType('already_registered');
          setErrors({
            ...errors,
            general: (
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-800">This email is already registered</p>
                  <button
                    type="button"
                    onClick={() => {
                      setIsRegistering(false);
                      setErrors({ email: '', password: '', general: '' });
                    }}
                    className="text-sm font-medium text-blue-600 hover:text-blue-500 transition-colors"
                  >
                    Log in instead →
                  </button>
                </div>
              </div>
            )
          });
        } else {
          setErrorType(null);
          setErrors({
            ...errors,
            general: (
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-red-800">
                    {error.message === 'Please login to continue' ? 'Incorrect email or password' : error.message}
                  </p>
                </div>
              </div>
            )
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateForm = () => {
    const newErrors = { email: '', password: '', general: '' };

    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    window.addEventListener('mousedown', handleClickOutside);
    return () => window.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        const emailInput = document.querySelector('input[type="email"]') as HTMLInputElement;
        emailInput?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setPassword('');
      setErrors({ email: '', password: '', general: '' });
      setIsRegistering(false);
      setTouchedFields({ email: false, password: false });
    }
  }, [isOpen]);

  const handleModeToggle = () => {
    setEmail('');
    setPassword('');
    setErrors({ email: '', password: '', general: '' });
    setTouchedFields({ email: false, password: false });
    setIsRegistering(!isRegistering);
  };

  const handleGoogleSuccess = async () => {
    try {
      console.log('Google sign-in successful, calling onAuthSuccess');
      onAuthSuccess();
      onClose();
    } catch (error) {
      console.error('Google sign-in failed:', error);
      setErrors(prev => ({
        ...prev,
        general: (
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-red-800">
                {error instanceof Error ? error.message : 'Failed to authenticate with Google'}
              </p>
            </div>
          </div>
        )
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-md overflow-y-auto h-full w-full flex items-center justify-center p-4 animate-in fade-in duration-300">
        <Card className="w-[400px] max-w-[90%] shadow-xl rounded-xl relative" ref={modalRef}>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100 p-1"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <CardHeader className="space-y-1 transition-all duration-300">
            <CardTitle className="text-2xl font-bold text-center transition-all duration-300">
              {isRegistering ? 'Create an account' : 'Welcome back!'}
            </CardTitle>
            <p className="text-gray-600 text-center text-sm">
              {isRegistering
                ? 'Enter your details to create your free account'
                : "Login to generate your podcast"}
            </p>
          </CardHeader>
          <form onSubmit={handleSubmit}>
            <fieldset disabled={isLoading} className="space-y-4">
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <label className="text-gray-900 text-sm font-medium leading-none flex items-center gap-1">
                    Email <span className="text-blue-500">*</span>
                  </label>
                  <Input
                    type="email"
                    placeholder="name@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      if (touchedFields.email) {
                        const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
                        if (emailRegex.test(e.target.value)) {
                          setErrors(prev => ({ ...prev, email: '' }));
                        }
                      }
                    }}
                    onBlur={() => setTouchedFields(prev => ({ ...prev, email: true }))}
                    className={`h-11 transition-all duration-200 focus:ring-2 focus:ring-blue-500/20 ${
                      touchedFields.email && errors.email ? 'border-red-500' : ''
                    }`}
                    aria-label="Email address"
                    aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "email-error" : undefined}
                  />
                  {touchedFields.email && errors.email && (
                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-gray-900 text-sm font-medium leading-none">
                      Password <span className="text-blue-500">*</span>
                    </label>
                    {!isRegistering && (
                      <button
                        type="button"
                        className="text-blue-500 text-sm hover:underline"
                        tabIndex={-1}
                      >
                        Forgot your password?
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value);
                        if (touchedFields.password && e.target.value.length >= 6) {
                          setErrors(prev => ({ ...prev, password: '' }));
                        }
                      }}
                      onBlur={() => {
                        setTouchedFields(prev => ({ ...prev, password: true }));
                        if (password.length < 6) {
                          setErrors(prev => ({
                            ...prev,
                            password: 'Password must be at least 6 characters long'
                          }));
                        }
                      }}
                      className={`h-11 ${
                        touchedFields.password && errors.password ? 'border-red-500' : ''
                      } pr-10`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3l18 18M10.5 15.5L9 17M3.51 15.5l9.98-9.98" />
                        </svg>
                      )}
                    </button>
                  </div>
                  {touchedFields.password && errors.password && (
                    <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                  )}
                </div>
                <Button
                  type="submit"
                  className="w-full h-11 bg-[#5865F2] hover:bg-[#4752C4] transform transition-all hover:scale-[1.02] active:scale-[0.98]"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/50 border-t-white rounded-full animate-spin" />
                      {isRegistering ? 'Creating Account...' : 'Logging in...'}
                    </div>
                  ) : (
                    isRegistering ? 'Sign Up' : 'Log In'
                  )}
                </Button>
              </CardContent>
            </fieldset>
          </form>
          {errors.general && (
            <div className="mx-6 mb-4 p-4 bg-red-50 border-l-4 border-red-400 rounded-r-lg">
              {errors.general}
            </div>
          )}
          <CardFooter className="flex flex-col space-y-4">
            {!isRegistering && errorType !== 'already_registered' && (
              <>
                <div className="text-center text-xs text-gray-500 my-2">
                  OR CONTINUE WITH
                </div>
                <div className="flex flex-col items-center gap-4">
                  <Button
                    variant="outline"
                    className="h-11 w-full max-w-[250px] border-gray-200 hover:bg-gray-50"
                  >
                    <FaGithub className="w-5 h-5 mr-2" />
                    Github
                  </Button>
                  <GoogleSignInButton
                    onSuccess={handleGoogleSuccess}
                  />
                </div>
              </>
            )}

            {errorType !== 'already_registered' && (
              <div className="text-center text-sm flex gap-1 justify-center">
                <span className="text-gray-500">
                  {isRegistering ? 'Already have an account?' : 'Need an account?'}
                </span>
                <button
                  type="button"
                  className="text-blue-500 hover:text-blue-600 font-medium hover:underline"
                  onClick={handleModeToggle}
                >
                  {isRegistering ? 'Log In' : 'Register'}
                </button>
              </div>
            )}
          </CardFooter>
        </Card>
      </div>
    </Modal>
  );
};

export default LoginModal;
